import { useMemo, useState } from 'react';

import { Button, ExcelIcon, type TypeSelectOption } from '@thefarmersfront/kitchen';

import { Table, TableBottomField, TableTopField } from '~/components/common';

import { useModal, useStableCallback } from '~/hooks/common';
import { useGetDeliveryResultReportDetailMutation } from '~/hooks/delivery-agency/queries';
import { getDeliveryAgencyReportsColumnDefs } from '~/utils/delivery-agency/table';

import { DELIVERY_AGENCY_PAGE_SIZE } from '~/constants/delivery-agency';

import DeliveryAgencyReportDetailModal from '../DeliveryAgencyReportDetailModal';

import type { TypePagination } from '~/types/common.types';
import type { DeliveryResultReportDetail, DeliveryResultReportItem } from '~/types/delivery-agency.types';

import styles from './DeliveryAgencyReportTable.module.scss';

/**
 * [배송 결과 리포트] 목록 테이블 컴포넌트
 */
const DeliveryAgencyReportTable = ({
  data,
  pagination,
  isDownloadReady,
  onChangePageLimit,
  onChangePage,
  onClickExcelDownload,
}: {
  data: DeliveryResultReportItem[] | null;
  pagination: TypePagination;
  isDownloadReady: boolean;
  onChangePageLimit: (value: TypeSelectOption<number>) => void;
  onChangePage: (value: number) => void;
  onClickExcelDownload: () => void;
}) => {
  const detailModal = useModal();
  const [detailData, setDetailData] = useState<DeliveryResultReportDetail | null>(null);

  const reportDetailMutation = useGetDeliveryResultReportDetailMutation({ showGlobalDimOverlay: false });

  const handleClickViewDetail = useStableCallback((report: DeliveryResultReportItem) => {
    reportDetailMutation.mutate(
      { reportId: report.id },
      {
        onSuccess: (detail) => {
          setDetailData(detail);
          detailModal.handleOpenModal();
        },
      }
    );
  });

  const { columnDefs } = useMemo(
    () =>
      getDeliveryAgencyReportsColumnDefs({
        currentPage: pagination.currentPage - 1,
        pageSize: pagination.pageSize,
        onClickViewDetail: handleClickViewDetail,
      }),
    [pagination.currentPage, pagination.pageSize, handleClickViewDetail]
  );

  return (
    <div className={styles['table-container']}>
      <TableTopField
        dataTotalCount={pagination.totalCount}
        rightButton={
          <Button
            size="tiny"
            variant="outlined"
            color="tertiary"
            disabled={!isDownloadReady}
            leftIcon={<ExcelIcon size={16} />}
            onClick={onClickExcelDownload}
          >
            리포트 전체 다운로드
          </Button>
        }
      />

      <div className={styles['table-wrapper']}>
        <Table data={data} options={{ columnDefs }} rowStatusType={data === null ? 'INIT' : 'EMPTY'} />
      </div>

      <TableBottomField
        pageSizeOptions={Object.values(DELIVERY_AGENCY_PAGE_SIZE)}
        currentPage={pagination.currentPage - 1}
        totalCount={pagination.totalCount}
        pageSize={pagination.pageSize}
        onChangePageLimit={onChangePageLimit}
        onClickPage={(page) => onChangePage(page + 1)}
      />

      {detailModal.isOpen && detailData && (
        <DeliveryAgencyReportDetailModal
          open={detailModal.isOpen}
          data={detailData}
          onClose={detailModal.handleCloseModal}
        />
      )}
    </div>
  );
};

export default DeliveryAgencyReportTable;
